/**
 * Scene linking + frame creation glue (Blocks 5, 6 & B/C). The imperative
 * helpers here mutate the open scene through the Excalidraw API; the pure,
 * unit-tested logic (frame types, main-frame resolution, preview links) lives in
 * frame-model.ts and is re-exported so existing imports keep working.
 *
 * Everything is host-side — no change under packages/excalidraw.
 */

import {
  CaptureUpdateAction,
  convertToExcalidrawElements,
  restoreElements,
  viewportCoordsToSceneCoords,
} from "@excalidraw/excalidraw";
import { duplicateElements } from "@excalidraw/element";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { FileId } from "@excalidraw/element/types";
import type { BinaryFileData } from "@excalidraw/excalidraw/types";

import {
  boardPath,
  computeImageBox,
  DISPLAY_MODE_KEY,
  EMBED_TARGET_KEY,
  FRAME_IMAGE_KEY,
  FRAME_TYPE_KEY,
  getFrameImageId,
  getImageFit,
  IMAGE_FIT_KEY,
  isFrameEl,
  SCENE_LINK_KEY,
} from "./frame-model";

import type {
  DisplayMode,
  EmbedTarget,
  FrameType,
  ImageFit,
  SceneLinkData,
  SceneLinkMode,
} from "./frame-model";

export * from "./frame-model";

type ElementWithMeta = ExcalidrawElement & {
  customData?: Record<string, unknown>;
  link?: string | null;
};

/** The single selected frame element, or null (0 or >1 selected → null). */
export const getSelectedFrame = (
  api: ExcalidrawImperativeAPI,
): ExcalidrawElement | null => {
  const selected = api.getAppState().selectedElementIds || {};
  const frames = api
    .getSceneElementsIncludingDeleted()
    .filter((el) => !el.isDeleted && isFrameEl(el) && selected[el.id]);
  return frames.length === 1 ? frames[0] : null;
};

/**
 * The single selected element of ANY type, or null (0 or >1 selected → null).
 * Drives the Element-Menu (CC_05 Block 6/7 — one on-select action island).
 */
export const getSelectedElement = (
  api: ExcalidrawImperativeAPI,
): ExcalidrawElement | null => {
  const selected = api.getAppState().selectedElementIds || {};
  const els = api
    .getSceneElementsIncludingDeleted()
    .filter((el) => !el.isDeleted && selected[el.id]);
  return els.length === 1 ? els[0] : null;
};

/** Set/replace the link on an element (CC_05 Block 6). */
const patchElement = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
  mutate: (el: ElementWithMeta) => ElementWithMeta,
): void => {
  const next = api
    .getSceneElementsIncludingDeleted()
    .map((el) => (el.id === elementId ? mutate(el as ElementWithMeta) : el));
  api.updateScene({
    elements: next,
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};

/** Link an element to an external URL (native Excalidraw link, D-30 a). */
export const setElementExternalLink = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
  url: string,
): void =>
  patchElement(api, elementId, (el) => {
    const meta = { ...(el.customData || {}) };
    delete meta[SCENE_LINK_KEY];
    return { ...el, link: url, customData: meta };
  });

/**
 * Link an element to an internal file (D-30 b). The link is the in-app board
 * path; the click handler routes by the TARGET's kind (Block 1), so a note
 * opens the MD editor, not the board — no overwrite. The scene-link customData
 * carries the display name.
 */
export const setElementInternalLink = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
  targetFileId: string,
  targetName?: string,
): void =>
  patchElement(api, elementId, (el) => ({
    ...el,
    link: boardPath(targetFileId),
    customData: {
      ...(el.customData || {}),
      [SCENE_LINK_KEY]: {
        targetFileId,
        targetName,
        mode: "button",
      } as SceneLinkData,
    },
  }));

/**
 * R4 (Punkt 15) — switch an EXISTING internal link between the button
 * presentation and the inline document preview. No-op on elements without a
 * scene link (the mode lives only in the scene-link customData).
 */
export const setSceneLinkMode = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
  mode: SceneLinkMode,
): void =>
  patchElement(api, elementId, (el) => {
    const meta = el.customData?.[SCENE_LINK_KEY] as SceneLinkData | undefined;
    if (!meta) {
      return el;
    }
    return {
      ...el,
      customData: {
        ...(el.customData || {}),
        [SCENE_LINK_KEY]: { ...meta, mode },
      },
    };
  });

/** Remove any link from an element (CC_05 Block 6). */
export const removeElementLink = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
): void =>
  patchElement(api, elementId, (el) => {
    const meta = { ...(el.customData || {}) };
    delete meta[SCENE_LINK_KEY];
    return { ...el, link: null, customData: meta };
  });

/** Rename a frame in place (CC_05 Block 7 — frame action in its own context). */
export const renameFrame = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
  name: string,
): void =>
  patchElement(
    api,
    elementId,
    (el) => ({ ...el, name } as ElementWithMeta & { name: string }),
  );

const viewportCenter = (api: ExcalidrawImperativeAPI) => {
  const state = api.getAppState();
  return viewportCoordsToSceneCoords(
    {
      clientX: (state.offsetLeft ?? 0) + (state.width ?? 800) / 2,
      clientY: (state.offsetTop ?? 0) + (state.height ?? 600) / 2,
    },
    state,
  );
};

/**
 * Create a new frame of a given RYFE type at the viewport centre (Block C.1).
 * Returns the new frame's element id.
 */
export const createFrame = (
  api: ExcalidrawImperativeAPI,
  type: FrameType,
  opts: { name?: string; embedTarget?: EmbedTarget } = {},
): string => {
  const c = viewportCenter(api);
  const w = 360;
  const hgt = 240;
  const customData: Record<string, unknown> = { [FRAME_TYPE_KEY]: type };
  if (type === "embed" && opts.embedTarget) {
    customData[EMBED_TARGET_KEY] = opts.embedTarget;
  }
  const [frame] = convertToExcalidrawElements([
    {
      type: "frame",
      x: c.x - w / 2,
      y: c.y - hgt / 2,
      width: w,
      height: hgt,
      name: opts.name,
      customData,
    } as never,
  ]);
  const withLink =
    type === "embed" && opts.embedTarget
      ? ({
          ...frame,
          link: boardPath(opts.embedTarget.targetFileId),
        } as ElementWithMeta)
      : frame;
  api.updateScene({
    elements: [...api.getSceneElementsIncludingDeleted(), withLink],
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
  return frame.id;
};

/** Update a frame's RYFE type and/or embed target in place (Block C.2). */
export const updateFrameMeta = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
  patch: { type?: FrameType; embedTarget?: EmbedTarget | null },
): void => {
  const next = api.getSceneElementsIncludingDeleted().map((el) => {
    if (el.id !== elementId) {
      return el;
    }
    const meta = { ...((el as ElementWithMeta).customData || {}) };
    let link = (el as ElementWithMeta).link ?? null;
    if (patch.type !== undefined) {
      meta[FRAME_TYPE_KEY] = patch.type;
      if (patch.type !== "embed") {
        delete meta[EMBED_TARGET_KEY];
        link = null;
      }
    }
    if (patch.embedTarget !== undefined) {
      if (patch.embedTarget) {
        meta[EMBED_TARGET_KEY] = patch.embedTarget;
        link = boardPath(patch.embedTarget.targetFileId);
      } else {
        delete meta[EMBED_TARGET_KEY];
        link = null;
      }
    }
    return { ...el, customData: meta, link } as ElementWithMeta;
  });
  api.updateScene({
    elements: next,
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};

/** Insert a scene-link element (button or preview) at the viewport center. */
export const insertSceneLink = (
  api: ExcalidrawImperativeAPI,
  data: SceneLinkData,
): void => {
  const center = viewportCenter(api);
  const isPreview = data.mode === "preview";
  const w = isPreview ? 320 : 240;
  const hgt = isPreview ? 200 : 88;
  const created = convertToExcalidrawElements(
    [
      {
        type: "rectangle",
        x: center.x - w / 2,
        y: center.y - hgt / 2,
        width: w,
        height: hgt,
        strokeColor: "#1971c2",
        backgroundColor: isPreview ? "#f8f9fa" : "#e7f5ff",
        roundness: null,
        label: {
          text: (isPreview ? "🖼 " : "🔗 ") + (data.targetName || "Szene"),
          verticalAlign: isPreview ? "top" : "middle",
        },
      },
    ],
    { regenerateIds: true },
  );
  const withMeta = created.map((el, i) =>
    i === 0
      ? ({
          ...el,
          link: boardPath(data.targetFileId),
          customData: { ...(el.customData || {}), [SCENE_LINK_KEY]: data },
        } as ElementWithMeta)
      : el,
  );
  api.updateScene({
    elements: [...api.getSceneElementsIncludingDeleted(), ...withMeta],
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};

/** Insert raw library elements as fresh copies (regenerated ids/seeds). */
export const insertLibraryElements = (
  api: ExcalidrawImperativeAPI,
  rawElements: unknown[],
): void => {
  const restored = restoreElements(rawElements as ExcalidrawElement[], null);
  if (restored.length === 0) {
    return;
  }
  const { duplicatedElements } = duplicateElements({
    type: "everything",
    elements: restored,
    randomizeSeed: true,
  });
  api.updateScene({
    elements: [
      ...api.getSceneElementsIncludingDeleted(),
      ...duplicatedElements,
    ],
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};

// ---------------------------------------------------------------------------
// CC_06 (F1/F4) — images as Excalidraw scene-assets (never a folder file). The
// binary lives in the scene's `files` map and is serialised into the board blob
// on S3 (board-data.serializeScene passes `files`), so it appears in the board
// but not in the folder listing. All host-side; nothing under packages/.
// ---------------------------------------------------------------------------

export interface UploadedImage {
  dataURL: string;
  mimeType: string;
  naturalWidth: number;
  naturalHeight: number;
  /** Monotonic stamp (no Date.now in tests) — pass a view/version counter. */
  created: number;
}

const addBinary = (
  api: ExcalidrawImperativeAPI,
  img: UploadedImage,
): FileId => {
  const fileId = `ryfe-img-${img.created}-${Math.round(
    img.naturalWidth,
  )}x${Math.round(img.naturalHeight)}` as FileId;
  const file: BinaryFileData = {
    id: fileId,
    dataURL: img.dataURL as BinaryFileData["dataURL"],
    mimeType: img.mimeType as BinaryFileData["mimeType"],
    created: img.created,
  };
  api.addFiles([file]);
  return fileId;
};

/**
 * CC_06 Block 2/3 — put an image INTO a frame (Bilderrahmen). Any prior image of
 * the frame is removed; the new image is sized per the frame's fit mode and
 * clipped by the frame. The frame records the scene-asset id (`FRAME_IMAGE_KEY`)
 * and its fit (`IMAGE_FIT_KEY`) in customData.
 */
export const setFrameImage = (
  api: ExcalidrawImperativeAPI,
  frameId: string,
  img: UploadedImage,
): void => {
  const els = api.getSceneElementsIncludingDeleted();
  const frame = els.find((e) => e.id === frameId);
  if (!frame || !isFrameEl(frame)) {
    return;
  }
  const fileId = addBinary(api, img);
  const fit = getImageFit(frame);
  const box = computeImageBox(
    { x: frame.x, y: frame.y, width: frame.width, height: frame.height },
    { width: img.naturalWidth, height: img.naturalHeight },
    fit,
  );
  // Drop the frame's previous image element(s).
  const withoutOld = els.filter(
    (e) =>
      !(e.type === "image" && (e as { frameId?: string }).frameId === frameId),
  );
  const [imageEl] = convertToExcalidrawElements([
    {
      type: "image",
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      fileId,
      frameId,
    } as never,
  ]);
  const nextFrame = {
    ...frame,
    customData: {
      ...((frame as ElementWithMeta).customData || {}),
      [FRAME_IMAGE_KEY]: fileId,
      [IMAGE_FIT_KEY]: fit,
    },
  } as ElementWithMeta;
  api.updateScene({
    elements: [
      ...withoutOld.map((e) => (e.id === frameId ? nextFrame : e)),
      imageEl,
    ],
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};

/** CC_06 Block 3 — change the fit of a frame's image and re-size it. */
export const setFrameImageFit = (
  api: ExcalidrawImperativeAPI,
  frameId: string,
  fit: ImageFit,
): void => {
  const els = api.getSceneElementsIncludingDeleted();
  const frame = els.find((e) => e.id === frameId);
  if (!frame || !isFrameEl(frame) || !getFrameImageId(frame)) {
    return;
  }
  const next = els.map((e) => {
    if (e.id === frameId) {
      return {
        ...e,
        customData: {
          ...((e as ElementWithMeta).customData || {}),
          [IMAGE_FIT_KEY]: fit,
        },
      } as ElementWithMeta;
    }
    if (e.type === "image" && (e as { frameId?: string }).frameId === frameId) {
      const box = computeImageBox(
        { x: frame.x, y: frame.y, width: frame.width, height: frame.height },
        { width: e.width || 1, height: e.height || 1 },
        fit,
      );
      return { ...e, x: box.x, y: box.y, width: box.width, height: box.height };
    }
    return e;
  });
  api.updateScene({
    elements: next,
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};

/**
 * CC_10 Block 1 (D-54) — set the in-scene display mode of an element. Stored in
 * the RYFE customData namespace (`DISPLAY_MODE_KEY`) so it round-trips through
 * board-data.serializeScene with no schema change; the host-render wrapper reads
 * it. Any element may carry it (an embed frame, a scene-link preview element).
 */
export const setDisplayMode = (
  api: ExcalidrawImperativeAPI,
  elementId: string,
  mode: DisplayMode,
): void =>
  patchElement(api, elementId, (el) => ({
    ...el,
    customData: {
      ...(el.customData || {}),
      [DISPLAY_MODE_KEY]: mode,
    },
  }));

/**
 * CC_06 Block 2 — insert a free-standing image at a position (used from a shape's
 * "Bild hochladen"): a scene-asset image element, not framed.
 */
export const insertSceneImage = (
  api: ExcalidrawImperativeAPI,
  img: UploadedImage,
  at: { x: number; y: number },
): void => {
  const fileId = addBinary(api, img);
  const [imageEl] = convertToExcalidrawElements([
    {
      type: "image",
      x: at.x,
      y: at.y,
      width: img.naturalWidth,
      height: img.naturalHeight,
      fileId,
    } as never,
  ]);
  api.updateScene({
    elements: [...api.getSceneElementsIncludingDeleted(), imageEl],
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};
